import React, { useState, useCallback } from 'react';
import { Button, Input } from '../../../ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

export interface SearchResult {
    place_id: string;
    name: string;
    geometry: { location: { lat: number; lng: number } };
    vicinity?: string;
    formatted_address?: string;
    rating?: number;
    types?: string[];
    alreadyInDb: boolean;
}

interface ClusterShopSearchProps {
    clusterId: string;
    onSearch: (id: string, query: string) => Promise<{ results: SearchResult[] }>;
    onAddPlaces: (id: string, places: Array<{ place_id: string; name: string; lat: number; lng: number; address?: string; types?: string[] }>) => Promise<{ added: number; skipped: number }>;
    onRefetchShops: () => void;
}

const ClusterShopSearch: React.FC<ClusterShopSearchProps> = ({
    clusterId,
    onSearch,
    onAddPlaces,
    onRefetchShops
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [adding, setAdding] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    const handleSearch = useCallback(async () => {
        if (!query.trim()) return;
        setSearching(true);
        setResults([]);
        setSelected(new Set());
        try {
            const { results: res } = await onSearch(clusterId, query.trim());
            setResults(res || []);
        } catch {
            toast.error('Search failed');
        } finally {
            setSearching(false);
        }
    }, [clusterId, query, onSearch]);

    const toggleSelect = useCallback((placeId: string, alreadyInDb: boolean) => {
        if (alreadyInDb) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(placeId)) next.delete(placeId);
            else next.add(placeId);
            return next;
        });
    }, []);

    const addableResults = results.filter((r) => !r.alreadyInDb);
    const selectedAddable = addableResults.filter((r) => selected.has(r.place_id));

    const handleAddSelected = useCallback(async () => {
        if (selectedAddable.length === 0) return;
        setAdding(true);
        try {
            const places = selectedAddable.map((r) => ({
                place_id: r.place_id,
                name: r.name,
                lat: r.geometry.location.lat,
                lng: r.geometry.location.lng,
                address: r.vicinity || r.formatted_address,
                types: r.types
            }));
            const { added, skipped } = await onAddPlaces(clusterId, places);
            toast.success(`Added ${added} shop(s)${skipped > 0 ? `, ${skipped} already in cluster` : ''}`);
            onRefetchShops();
            setSelected(new Set());
            setResults((prev) =>
                prev.map((r) =>
                    selectedAddable.some((a) => a.place_id === r.place_id) ? { ...r, alreadyInDb: true } : r
                )
            );
        } catch {
            toast.error('Failed to add places');
        } finally {
            setAdding(false);
        }
    }, [clusterId, selectedAddable, selected, onAddPlaces, onRefetchShops]);

    const handleAddAllNew = useCallback(async () => {
        if (addableResults.length === 0) return;
        setAdding(true);
        try {
            const places = addableResults.map((r) => ({
                place_id: r.place_id,
                name: r.name,
                lat: r.geometry.location.lat,
                lng: r.geometry.location.lng,
                address: r.vicinity || r.formatted_address,
                types: r.types
            }));
            const { added, skipped } = await onAddPlaces(clusterId, places);
            toast.success(`Added ${added} shop(s)${skipped > 0 ? `, ${skipped} already in cluster` : ''}`);
            onRefetchShops();
            setResults((prev) =>
                prev.map((r) => ({ ...r, alreadyInDb: true }))
            );
        } catch {
            toast.error('Failed to add places');
        } finally {
            setAdding(false);
        }
    }, [clusterId, addableResults, onAddPlaces, onRefetchShops]);

    return (
        <div className="rounded-lg border border-ink/15 p-4 bg-muted/20">
            <h4 className="text-sm font-medium mb-3">Search and add places</h4>
            <div className="flex gap-2 mb-3">
                <Input
                    placeholder="e.g. roastery, coffee shop name..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1"
                />
                <Button size="sm" onClick={handleSearch} disabled={searching}>
                    {searching ? (
                        <FontAwesomeIcon icon={faSearch} className="animate-pulse" />
                    ) : (
                        <FontAwesomeIcon icon={faSearch} className="mr-1.5" />
                    )}
                    Search
                </Button>
            </div>

            {results.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                            {results.length} result(s), {addableResults.length} new
                        </span>
                        <div className="flex gap-2">
                            {selectedAddable.length > 0 && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleAddSelected}
                                    disabled={adding}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
                                    Add selected ({selectedAddable.length})
                                </Button>
                            )}
                            {addableResults.length > 0 && (
                                <Button size="sm" onClick={handleAddAllNew} disabled={adding}>
                                    <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
                                    Add all new ({addableResults.length})
                                </Button>
                            )}
                        </div>
                    </div>
                    <ul className="max-h-48 overflow-y-auto space-y-1 border border-ink/10 rounded p-2">
                        {results.map((r) => (
                            <li
                                key={r.place_id}
                                className={`flex items-center gap-2 p-2 rounded text-sm ${
                                    r.alreadyInDb ? 'bg-muted/30 text-muted-foreground' : 'hover:bg-muted/20'
                                }`}
                            >
                                {!r.alreadyInDb && (
                                    <button
                                        type="button"
                                        onClick={() => toggleSelect(r.place_id, r.alreadyInDb)}
                                        className="w-4 h-4 rounded border border-ink/30 flex items-center justify-center shrink-0"
                                    >
                                        {selected.has(r.place_id) && (
                                            <FontAwesomeIcon icon={faCheck} className="h-2.5 w-2.5" />
                                        )}
                                    </button>
                                )}
                                {r.alreadyInDb && (
                                    <span className="w-4 flex items-center justify-center shrink-0 text-green-600">
                                        <FontAwesomeIcon icon={faCheck} />
                                    </span>
                                )}
                                <div className="flex-1 min-w-0">
                                    <span className="font-medium">{r.name}</span>
                                    {(r.vicinity || r.formatted_address) && (
                                        <span className="text-muted-foreground ml-1">
                                            · {r.vicinity || r.formatted_address}
                                        </span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ClusterShopSearch;
